import { supabase } from '@/lib/supabase/client';
import type {
  PlacementAudit,
  PlacementAuditInsert,
  PlacementAuditStatus,
  PlacementAuditUpdate,
} from '@/types/customer-ops';

function nullableText(value: string | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  const trimmed = value.trim();
  return trimmed || null;
}

export const placementAuditsService = {
  async list(): Promise<PlacementAudit[]> {
    try {
      const { data, error } = await supabase
        .from('placement_audits')
        .select('*')
        .order('updated_at', { ascending: false });

      if (error) {
        console.error('Error fetching placement audits:', error);
        return [];
      }
      return (data ?? []) as PlacementAudit[];
    } catch (error) {
      console.error('Error fetching placement audits:', error);
      return [];
    }
  },

  async getById(id: string): Promise<PlacementAudit | null> {
    try {
      const { data, error } = await supabase.from('placement_audits').select('*').eq('id', id).maybeSingle();
      if (error) {
        console.error('Error fetching placement audit:', error);
        return null;
      }
      return (data as PlacementAudit | null) ?? null;
    } catch (error) {
      console.error('Error fetching placement audit:', error);
      return null;
    }
  },

  async create(payload: PlacementAuditInsert): Promise<PlacementAudit> {
    const insertRow = {
      location_id: payload.location_id,
      status: payload.status ?? 'requested',
      trigger_reason: payload.trigger_reason ?? 'cs_recommendation',
      owner: nullableText(payload.owner),
      engagement_context: payload.engagement_context?.trim() ?? '',
      findings_notes: payload.findings_notes?.trim() ?? '',
      report_url: nullableText(payload.report_url),
      scheduled_date: nullableText(payload.scheduled_date),
      completed_at: payload.completed_at ?? null,
    };

    const { data, error } = await supabase.from('placement_audits').insert(insertRow).select('*').single();
    if (error) {
      throw new Error(`Failed to create placement audit: ${error.message}`);
    }
    return data as PlacementAudit;
  },

  async update(id: string, payload: PlacementAuditUpdate): Promise<PlacementAudit> {
    const updateRow: Record<string, unknown> = {};
    if (payload.status !== undefined) updateRow.status = payload.status;
    if (payload.trigger_reason !== undefined) updateRow.trigger_reason = payload.trigger_reason;
    if (payload.owner !== undefined) updateRow.owner = nullableText(payload.owner);
    if (payload.engagement_context !== undefined) updateRow.engagement_context = payload.engagement_context.trim();
    if (payload.findings_notes !== undefined) updateRow.findings_notes = payload.findings_notes.trim();
    if (payload.report_url !== undefined) updateRow.report_url = nullableText(payload.report_url);
    if (payload.scheduled_date !== undefined) updateRow.scheduled_date = nullableText(payload.scheduled_date);
    if (payload.completed_at !== undefined) updateRow.completed_at = payload.completed_at;

    if (payload.status === 'complete' && payload.completed_at === undefined) {
      updateRow.completed_at = new Date().toISOString();
    }

    const { data, error } = await supabase.from('placement_audits').update(updateRow).eq('id', id).select('*').single();
    if (error) {
      throw new Error(`Failed to update placement audit: ${error.message}`);
    }
    return data as PlacementAudit;
  },

  async setStatus(id: string, status: PlacementAuditStatus): Promise<PlacementAudit> {
    return this.update(id, {
      status,
      completed_at: status === 'complete' ? new Date().toISOString() : null,
    });
  },
};
